import { useLocalSearchParams } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";

import DropDownPlayerView from "./DropDownPlayerView";
import { TriggerButton } from "./TriggerButton";

import { PlayerCardData } from "@/types/playerCardTypes";
import { useTheme } from "@/providers/ThemeProvider";

/**
 *
 */
interface Props {
  myRosterLoading: boolean;
  watchlistLoading: boolean;
  myRoster?: PlayerCardData[];
  watchList?: PlayerCardData[];
  selectedComparisonPlayer: PlayerCardData | null;
  onSelectComparisonPlayer: (player: PlayerCardData) => void;
  onLoadMore: (value: string) => void;
}

/**
 * Container component for player selection dropdown in compare players feature
 * Manages dropdown state, computes bounded popover height from screen dimensions,
 * and handles player selection from watchlist and roster
 * @param root0 - Component props
 * @param root0.myRoster - Array of players from user's roster
 * @param root0.watchList - Array of players from selected watchlist
 * @param root0.myRosterLoading - Loading state for roster data
 * @param root0.watchlistLoading - Loading state for watchlist data
 * @param root0.selectedComparisonPlayer - Currently selected player for comparison
 * @param root0.onSelectComparisonPlayer - Callback when a player is selected from dropdown
 * @param root0.onLoadMore - Callback to load more players when scrolling to end of list
 */
const DropDownPlayerContainer = ({
  myRoster,
  watchList,
  myRosterLoading,
  watchlistLoading,
  selectedComparisonPlayer,
  onSelectComparisonPlayer,
  onLoadMore,
}: Props) => {
  const { watchlistName } = useLocalSearchParams<{
    watchlistName: string;
  }>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { height: windowHeight } = useWindowDimensions();

  const popoverMaxHeight = useMemo(
    () => Math.round(windowHeight * 0.6),
    [windowHeight]
  );

  const selectedComparisonPlayerLabel = useMemo(
    () =>
      selectedComparisonPlayer
        ? selectedComparisonPlayer.name
        : "Select Player",
    [selectedComparisonPlayer]
  );

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSelectComparisonPlayer = useCallback(
    (player: PlayerCardData) => {
      onSelectComparisonPlayer(player);
      handleClose();
    },
    [handleClose, onSelectComparisonPlayer]
  );

  const handleTriggerPress = useCallback(
    (event: unknown, triggerOnPress?: (event: unknown) => void) => {
      triggerOnPress?.(event);
      handleOpen();
    },
    [handleOpen]
  );

  const renderTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => {
      const { onPress: triggerOnPress, ...restTriggerProps } = triggerProps as {
        onPress?: (event: unknown) => void;
      };

      return (
        <TriggerButton
          restTriggerProps={restTriggerProps}
          triggerOnPress={triggerOnPress}
          onTriggerPress={handleTriggerPress}
          selectedComparisonPlayerLabel={selectedComparisonPlayerLabel}
        />
      );
    },
    [selectedComparisonPlayerLabel, handleTriggerPress]
  );

  return (
    <DropDownPlayerView
      isOpen={isOpen}
      selectedComparisonPlayerLabel={selectedComparisonPlayerLabel}
      listWatchlistPlayers={watchList}
      listMyRosterPlayers={myRoster}
      onOpen={handleOpen}
      onClose={handleClose}
      onLoadMore={onLoadMore}
      watchListName={watchlistName}
      loadingWatchList={watchlistLoading}
      loadingMyRoster={myRosterLoading}
      onSelectComparePlayer={handleSelectComparisonPlayer}
      isDark={isDark}
      renderTrigger={renderTrigger}
      popoverMaxHeight={popoverMaxHeight}
    />
  );
};

export default DropDownPlayerContainer;
